<template>
  <div class="pay-range-drawer-header">
    <div class="pay-range-drawer-header__top">
      <div class="pay-range-drawer-header__heading">
        <h2 class="pay-range-drawer-header__title" data-test="header-title">{{ title }}</h2>
        <span
          v-if="subtitle"
          class="pay-range-drawer-header__subtitle"
          data-test="header-subtitle"
        >{{ subtitle }}</span>

        <div
          v-if="showBadges"
          class="pay-range-drawer-header__badges"
          role="group"
          aria-label="Version, status and state"
          data-test="header-badges"
        >
          <VersionBadge :code="versionCode || '—'" data-test="header-version-badge" />
          <PayPlanLifecycleBadge
            :status="statusBadge.tone"
            :label="statusBadge.label"
            data-test="header-status-badge"
          />
          <PayPlanPeriodStateBadge
            v-if="showStateBadge"
            :state="stateBadge || 'Future'"
            :filled="isDraft"
            data-test="header-state-badge"
          />
          <PayRangeCorrectedBadge
            v-if="corrected"
            :corrected-at="correctedAt"
            :corrected-by-name="correctedByName"
            data-test="header-corrected-badge"
          />
        </div>

        <span
          v-if="effectiveFromLabel"
          class="pay-range-drawer-header__effective"
          data-test="header-effective-from"
        >Effective from {{ effectiveFromLabel }}</span>
      </div>

      <div class="pay-range-drawer-header__actions">
        <ChatterIcon
          v-if="showChatter"
          class="pay-range-drawer-header__chatter"
          :unread="unread"
          :count="count"
          data-test="header-chatter"
          @click="$emit('chatter')"
        />
        <button
          type="button"
          class="pay-range-drawer-header__close"
          aria-label="Close drawer"
          data-test="header-close"
          @click="$emit('close')"
        >
          <v-icon size="20" color="#98a2b3">mdi-close</v-icon>
        </button>
      </div>
    </div>

    <PeriodBar
      v-if="showPeriodBar"
      class="pay-range-drawer-header__period"
      :state="periodBarState"
      :label="periodBarLabel"
      :open-ended="openEnded"
      :outlined="periodBarOutlined"
      :selectable="false"
    />
  </div>
</template>

<script setup>
import { computed } from "vue";
import ChatterIcon from "@components/ChatterIcon/ChatterIcon.vue";
import PeriodBar from "@components/PeriodBar/PeriodBar.vue";
import PayPlanLifecycleBadge from "@components/PayPlanLifecycleBadge/PayPlanLifecycleBadge.vue";
import PayPlanPeriodStateBadge from "@components/PayPlanPeriodStateBadge/PayPlanPeriodStateBadge.vue";
import PayRangeCorrectedBadge from "@components/PayRangeCorrectedBadge/PayRangeCorrectedBadge.vue";
import VersionBadge from "@components/VersionBadge/VersionBadge.vue";
import { formatFairPayDate } from "@components/utils/fairPayFormat";
import { versionStatusBadge } from "@components/FairPay/payRangeBadges";

const props = defineProps({
  title: { type: String, default: "Pay range" },
  subtitle: { type: String, default: "" },
  step: { type: Number, default: 1 },
  versionCode: { type: String, default: null },
  status: { type: String, default: "Draft" },
  stateBadge: { type: String, default: "Future" },
  /** Effective from date (YYYY-MM-DD or Date), shown after the badges. Hidden when empty. */
  effectiveFrom: { type: [String, Date], default: null },
  showBadges: { type: Boolean, default: false },
  showStateBadge: { type: Boolean, default: true },
  showChatter: { type: Boolean, default: true },
  showPeriodBar: { type: Boolean, default: false },
  periodBarState: { type: String, default: "draft" },
  periodBarLabel: { type: String, default: "" },
  periodBarOutlined: { type: Boolean, default: false },
  openEnded: { type: Boolean, default: true },
  unread: { type: Number, default: 0 },
  count: { type: Number, default: null },
  /** The version was corrected in place: shows the Corrected badge after State. */
  corrected: { type: Boolean, default: false },
  correctedAt: { type: [String, Date], default: null },
  correctedByName: { type: String, default: "" },
});

defineEmits(["close", "chatter"]);

const effectiveFromLabel = computed(() => formatFairPayDate(props.effectiveFrom));

const statusBadge = computed(
  () => versionStatusBadge(props.status || "draft") || versionStatusBadge("in-force")
);
const isDraft = computed(() => statusBadge.value.tone === "draft");
</script>

<style scoped>
.pay-range-drawer-header {
  display: flex;
  flex-direction: column;
  gap: 11px;
  padding: 0 24px;
}
.pay-range-drawer-header__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.pay-range-drawer-header__actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 12px;
}
.pay-range-drawer-header__close {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  line-height: 0;
  border-radius: 8px;
}
.pay-range-drawer-header__close:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}
.pay-range-drawer-header__title {
  margin: 0;
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  color: #101828;
}
.pay-range-drawer-header__heading {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  flex: 1;
  min-width: 0;
  column-gap: 12px;
  row-gap: 4px;
}
.pay-range-drawer-header__subtitle {
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #475467;
}
.pay-range-drawer-header__badges {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.pay-range-drawer-header__effective {
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #475467;
}
</style>
